import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "@studio-freight/lenis";
import {
  Heart,
  Sparkles,
  Palette,
  Users,
  Phone,
  Mail,
  MapPin,
  ChevronDown,
  Star,
  ArrowRight,
  Clock,
  Calendar,
  Award,
  MessageCircle,
  Leaf,
  Sun,
  Feather } from
"lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger } from
"./components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious } from
"./components/ui/carousel";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pillar icons mapping
const pillarIcons = {
  "Bem-estar": Sun,
  "Prazer de Viver": Heart,
  "Criatividade": Palette,
  "Afetividade": Users
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.15 } },
  viewport: { once: true }
};

// Hero Section
const HeroSection = ({ scrollToSection }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      <motion.div
        className="hero-bg"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1700305510883-e7b0c8f10611?q=85&w=1920&auto=format&fit=crop')`,
          y
        }} />

      <div className="hero-overlay" />
      
      <motion.div
        className="relative z-10 text-center px-6 max-w-4xl mx-auto"
        style={{ opacity }}>

        <motion.p
          className="font-accent text-lg md:text-xl tracking-[0.3em] text-[#E8E4D9] mb-6 uppercase"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}>

          Mentoria de Vida
        </motion.p>
        
        <motion.h1
          className="text-5xl md:text-7xl font-normal tracking-tight text-white mb-6 leading-[1.1]"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}>

          Bioarte
        </motion.h1>
        
        <motion.p
          className="font-accent text-2xl md:text-3xl italic text-[#E8E4D9] mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}>

          Celebrando a Alegria de Viver
        </motion.p>
        
        <motion.p
          className="text-base md:text-lg text-[#E8E4D9]/80 max-w-2xl mx-auto mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}>

          Uma jornada para transformar sua vida através da reeducação funcional, no seu ritmo
        </motion.p>
        
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}>

          <Button
            onClick={() => scrollToSection('contact')}
            className="cta-primary text-base"
            data-testid="hero-cta-primary">

            Agendar Avaliação Gratuita
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            onClick={() => scrollToSection('about')}
            variant="outline"
            className="cta-secondary bg-transparent border-white text-white hover:bg-white hover:text-[#2C3E30] text-base"
            data-testid="hero-cta-secondary">

            Conhecer a Mentoria
          </Button>
        </motion.div>
      </motion.div>
      
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}>

        <ChevronDown className="h-8 w-8 text-white/60" />
      </motion.div>
    </section>);

};

// Benefits Section
const BenefitsSection = () => {
  const pillars = [
  {
    title: "Bem-estar",
    description: "Desenvolva práticas que nutrem corpo, mente e espírito para uma vida equilibrada.",
    icon: Sun
  },
  {
    title: "Prazer de Viver",
    description: "Redescubra a alegria nas pequenas coisas e cultive uma existência prazerosa.",
    icon: Heart
  },
  {
    title: "Criatividade",
    description: "Desbloqueie seu potencial criativo e encontre soluções inovadoras para a vida.",
    icon: Palette
  },
  {
    title: "Afetividade",
    description: "Fortaleça seus vínculos e desenvolva relacionamentos mais profundos e significativos.",
    icon: Users
  }];


  return (
    <section id="benefits" className="section-light py-20 md:py-32" data-testid="benefits-section">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          className="text-center mb-16"
          {...fadeInUp}>

          <p className="font-accent text-lg tracking-[0.2em] text-[#C26D53] mb-4 uppercase">Os Quatro Pilares</p>
          <h2 className="text-4xl md:text-5xl tracking-tight text-[#2C3E30] mb-6">
            Sistema Bioarte
          </h2>
          <p className="text-base md:text-lg text-[#595959] max-w-2xl mx-auto">
            Um método transdisciplinar que integra Psicologia, Biologia, Filosofia e Arte para uma vida plena.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}>

          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={pillar.title}
                className="benefit-card"
                variants={fadeInUp}
                data-testid={`pillar-card-${index}`}>

                <div className="pillar-icon">
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium text-[#2C3E30] mb-3">
                  {pillar.title}
                </h3>
                <p className="text-[#595959] leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>);

          })}
        </motion.div>
      </div>
    </section>);

};

// Modules Section
const ModulesSection = () => {
  const modules = [
  {
    number: "I",
    title: "A Base para o Florescimento",
    level: "Básico",
    duration: "No seu ritmo",
    description: "Construção dos fundamentos essenciais para a prática Bioarte e início da reeducação funcional.",
    topics: ["Autoconhecimento profundo", "Fundamentos do bem-estar", "Práticas de presença"]
  },
  {
    number: "II",
    title: "Aprofundando a Prática",
    level: "Intermediário",
    duration: "No seu ritmo",
    description: "Aprofundamento dos conhecimentos e integração das práticas de forma mais complexa no cotidiano.",
    topics: ["Integração das áreas da vida", "Desenvolvimento da criatividade", "Relações afetivas"]
  },
  {
    number: "III",
    title: "Rumo à Maestria",
    level: "Avançado",
    duration: "No seu ritmo",
    description: "Consolidação da sabedoria na arte de viver, culminando com titulação que reconhece sua jornada.",
    topics: ["Maestria pessoal", "Legado e propósito", "Titulação Bioarte"]
  }];


  return (
    <section id="modules" className="section-cream py-20 md:py-32 grain-overlay" data-testid="modules-section">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10">
        <motion.div
          className="text-center mb-16"
          {...fadeInUp}>

          <p className="font-accent text-lg tracking-[0.2em] text-[#C26D53] mb-4 uppercase">A Jornada</p>
          <h2 className="text-4xl md:text-5xl tracking-tight text-[#2C3E30] mb-6">
            Sua Transformação, Seu Ritmo
          </h2>
          <p className="text-base md:text-lg text-[#595959] max-w-2xl mx-auto">
            Um caminho estruturado em três módulos que você percorre conforme sua disponibilidade. Comece quando quiser e avance no seu próprio tempo.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}>

          {modules.map((module, index) =>
          <motion.div
            key={module.number}
            className="module-card p-8"
            variants={fadeInUp}
            data-testid={`module-card-${index}`}>

              <div className="flex items-center gap-4 mb-6">
                <span className="font-accent text-5xl text-[#C26D53]">{module.number}</span>
                <div>
                  <span className="text-sm tracking-wider text-[#595959] uppercase">{module.level}</span>
                  <div className="flex items-center gap-2 text-sm text-[#2C3E30]">
                    <Clock className="h-4 w-4" strokeWidth={1.5} />
                    {module.duration}
                  </div>
                </div>
              </div>
              
              <h3 className="text-2xl text-[#2C3E30] mb-4">{module.title}</h3>
              <p className="text-[#595959] mb-6 leading-relaxed">{module.description}</p>
              
              <ul className="space-y-2">
                {module.topics.map((topic, i) =>
              <li key={i} className="flex items-center gap-2 text-sm text-[#595959]">
                    <Leaf className="h-4 w-4 text-[#C26D53]" strokeWidth={1.5} />
                    {topic}
                  </li>
              )}
              </ul>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          className="mt-16 text-center"
          {...fadeInUp}>

          <div className="inline-flex flex-col sm:flex-row items-center gap-6 bg-white px-8 py-6 shadow-sm">
            <div className="text-center sm:text-left">
              <p className="text-sm text-[#595959] mb-1">Investimento mensal</p>
              <p className="text-3xl font-medium text-[#2C3E30]">R$ 300</p>
            </div>
            <div className="hidden sm:block h-12 w-px bg-[#E2E0D6]" />
            <div className="text-center sm:text-left">
              <p className="text-sm text-[#595959] mb-1">Formatos disponíveis</p>
              <p className="text-base text-[#2C3E30]">Presencial ou Online</p>
            </div>
            <div className="hidden sm:block h-12 w-px bg-[#E2E0D6]" />
            <div className="text-center sm:text-left">
              <p className="text-sm text-[#595959] mb-1">Flexibilidade</p>
              <p className="text-base text-[#2C3E30]">Comece quando quiser</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>);

};

// Mentor Section
const MentorSection = () => {
  return (
    <section id="mentor" className="section-light py-20 md:py-32" data-testid="mentor-section">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>

            <div className="aspect-[4/5] overflow-hidden grain-overlay">
              <img
                src="https://customer-assets.emergentagent.com/job_coaching-page/artifacts/uhqj9iy8_Bioarte%20Site.png"
                alt="Professor Luiz Carlos Faria"
                className="w-full h-full object-cover" />

            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#C26D53] text-white p-6">
              <p className="font-accent text-sm tracking-wider uppercase mb-1">Criador do</p>
              <p className="text-xl">Sistema Bioarte</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}>

            <p className="font-accent text-lg tracking-[0.2em] text-[#C26D53] mb-4 uppercase">Seu Mentor</p>
            <h2 className="text-4xl md:text-5xl tracking-tight text-[#2C3E30] mb-6">
              Professor Luiz Carlos Faria
            </h2>
            
            <div className="space-y-4 text-[#595959] leading-relaxed">
              <p>
                Criador e gestor do Sistema Bioarte, o Professor Luiz Carlos Faria dedica sua vida ao estudo e prática da arte de viver bem. Com décadas de experiência em desenvolvimento humano, ele integra conhecimentos de diversas áreas para criar uma metodologia única e transformadora.
              </p>
              <p>
                Sua abordagem transdisciplinar une Psicologia, Biologia, Filosofia e Arte, oferecendo um caminho completo para quem busca uma vida mais plena, criativa e significativa.
              </p>
              <p>
                O Professor Luiz Carlos conduz pessoalmente a avaliação inicial de cada mentorado, garantindo um acompanhamento verdadeiramente personalizado desde o primeiro momento.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <Award className="h-5 w-5 text-[#C26D53]" strokeWidth={1.5} />
                <span className="text-sm text-[#2C3E30]">Metodologia Própria</span>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-[#C26D53]" strokeWidth={1.5} />
                <span className="text-sm text-[#2C3E30]">+40 Anos de Experiência</span>
              </div>
            </div>

            <p className="font-accent text-2xl italic text-[#2C3E30] mt-10">
              "A vida plena é uma arte que pode ser aprendida."
            </p>
          </motion.div>
        </div>
      </div>
    </section>);

};

// Testimonials Section
const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
        // Fallback data
        setTestimonials([
        {
          id: "1",
          name: "Maria Helena S.",
          role: "Empresária, 52 anos",
          text: "A mentoria Bioarte transformou minha relação comigo mesma e com minha família. Hoje vivo com mais presença e alegria.",
          rating: 5
        },
        {
          id: "2",
          name: "Carlos Eduardo M.",
          role: "Médico, 45 anos",
          text: "Após anos focado apenas na carreira, encontrei no Sistema Bioarte o equilíbrio que buscava.",
          rating: 5
        },
        {
          id: "3",
          name: "Ana Paula R.",
          role: "Professora, 38 anos",
          text: "Iniciei a mentoria em um momento de crise existencial. Hoje, após 12 meses, sinto que renasci.",
          rating: 5
        }]
        );
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <section id="testimonials" className="section-dark py-20 md:py-32" data-testid="testimonials-section">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <motion.div
          className="text-center mb-16"
          {...fadeInUp}>

          <p className="font-accent text-lg tracking-[0.2em] text-[#C26D53] mb-4 uppercase">Depoimentos</p>
          <h2 className="text-4xl md:text-5xl tracking-tight text-[#E8E4D9] mb-6">
            Histórias de Transformação
          </h2>
          <p className="text-base md:text-lg text-[#E8E4D9]/70 max-w-2xl mx-auto">
            Veja como a mentoria Bioarte tem impactado a vida de nossos mentorados.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}>

          <Carousel
            opts={{
              align: "start",
              loop: true
            }}
            className="w-full">

            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial) =>
              <CarouselItem key={testimonial.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="testimonial-card h-full" data-testid={`testimonial-${testimonial.id}`}>
                    <div className="flex gap-1 mb-4 pt-8">
                      {[...Array(testimonial.rating)].map((_, i) =>
                    <Star key={i} className="h-4 w-4 fill-[#C26D53] text-[#C26D53]" />
                    )}
                    </div>
                    <p className="text-[#595959] leading-relaxed mb-6 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="mt-auto">
                      <p className="font-medium text-[#2C3E30]">{testimonial.name}</p>
                      <p className="text-sm text-[#595959]">{testimonial.role}</p>
                    </div>
                  </div>
                </CarouselItem>
              )}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-12 bg-white text-[#2C3E30] hover:bg-[#E8E4D9] border-[#E2E0D6]" />
            <CarouselNext className="hidden md:flex -right-12 bg-white text-[#2C3E30] hover:bg-[#E8E4D9] border-[#E2E0D6]" />
          </Carousel>
        </motion.div>
      </div>
    </section>);

};

// FAQ Section
const FAQSection = () => {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(`${API}/faq`);
        setFaqs(response.data);
      } catch (error) {
        console.error("Error fetching FAQs:", error);
        setFaqs([
        {
          question: "O que é a Mentoria Bioarte?",
          answer: "A Mentoria Bioarte é um programa de reeducação funcional para a vida com duração de 18 meses."
        },
        {
          question: "Qual a duração e estrutura do programa?",
          answer: "O programa tem duração total de 18 meses, dividido em 3 módulos de 6 meses cada."
        },
        {
          question: "Qual o investimento?",
          answer: "O investimento é de R$ 300 mensais."
        }]
        );
      }
    };
    fetchFAQs();
  }, []);

  return (
    <section id="faq" className="section-light py-20 md:py-32" data-testid="faq-section">
      <div className="container mx-auto px-6 md:px-12 max-w-4xl">
        <motion.div
          className="text-center mb-16"
          {...fadeInUp}>

          <p className="font-accent text-lg tracking-[0.2em] text-[#C26D53] mb-4 uppercase">Dúvidas</p>
          <h2 className="text-4xl md:text-5xl tracking-tight text-[#2C3E30] mb-6">
            Perguntas Frequentes
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}>

          <Accordion type="single" collapsible className="faq-accordion space-y-4">
            {faqs.map((faq, index) =>
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="border border-[#E2E0D6] px-6"
              data-testid={`faq-item-${index}`}>

                <AccordionTrigger className="text-left text-[#2C3E30] hover:text-[#C26D53] hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#595959] leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </motion.div>
      </div>
    </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("Mensagem enviada!", {
          description: "Entraremos em contato em breve."
        });
        setFormData({ name: "", email: "", phone: "", message: "" });
      }
    } catch (error) {
      toast.error("Erro ao enviar", {
        description: "Tente novamente ou entre em contato via WhatsApp."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-cream py-20 md:py-32 grain-overlay" data-testid="contact-section">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>

            <p className="font-accent text-lg tracking-[0.2em] text-[#C26D53] mb-4 uppercase">Vamos Conversar</p>
            <h2 className="text-4xl md:text-5xl tracking-tight text-[#2C3E30] mb-6">
              Agende Sua Avaliação
            </h2>
            <p className="text-base md:text-lg text-[#595959] mb-8 leading-relaxed">
              O primeiro passo para sua transformação é uma avaliação inicial gratuita com o Professor Luiz Carlos Faria. Preencha o formulário ou entre em contato diretamente pelo WhatsApp.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white flex items-center justify-center">
                  <Phone className="h-5 w-5 text-[#C26D53]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-sm text-[#595959]">WhatsApp</p>
                  <a href="https://wa.me/553199540016" className="text-[#2C3E30] hover:text-[#C26D53] transition-colors">(31) 99954-0016

                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}>

            <form onSubmit={handleSubmit} className="contact-form bg-white p-8 shadow-sm space-y-6" data-testid="contact-form">
              <div>
                <Label htmlFor="name" className="text-[#2C3E30] mb-2 block">Nome completo</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Seu nome"
                  required
                  className="border-[#E2E0D6] focus:border-[#C26D53] focus:ring-[#C26D53]/20"
                  data-testid="contact-name-input" />

              </div>

              <div>
                <Label htmlFor="email" className="text-[#2C3E30] mb-2 block">E-mail</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="seu@email.com"
                  required
                  className="border-[#E2E0D6] focus:border-[#C26D53] focus:ring-[#C26D53]/20"
                  data-testid="contact-email-input" />

              </div>

              <div>
                <Label htmlFor="phone" className="text-[#2C3E30] mb-2 block">WhatsApp</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(00) 00000-0000"
                  required
                  className="border-[#E2E0D6] focus:border-[#C26D53] focus:ring-[#C26D53]/20"
                  data-testid="contact-phone-input" />

              </div>

              <div>
                <Label htmlFor="message" className="text-[#2C3E30] mb-2 block">Mensagem (opcional)</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Conte um pouco sobre você e seus objetivos..."
                  rows={4}
                  className="border-[#E2E0D6] focus:border-[#C26D53] focus:ring-[#C26D53]/20 resize-none"
                  data-testid="contact-message-input" />

              </div>

              <Button
                type="submit"
                className="w-full cta-primary"
                disabled={isSubmitting}
                data-testid="contact-submit-btn">

                {isSubmitting ? "Enviando..." : "Enviar Mensagem"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>);

};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#2C3E30] py-12" data-testid="footer">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-center md:text-left">
            <h3 className="text-2xl text-[#E8E4D9] mb-2">Bioarte</h3>
            <p className="font-accent text-[#E8E4D9]/60 italic">Celebrando a Alegria de Viver</p>
          </div>
          
          <div className="text-center md:text-right text-[#E8E4D9]/60 text-sm">
            <p>&copy; {new Date().getFullYear()} Bioarte - Mentoria de Vida</p>
            <p>Todos os direitos reservados</p>
          </div>
        </div>
      </div>
    </footer>);

};

// WhatsApp Float Button
const WhatsAppFloat = () => {
  return (
    <a
      href="https://wa.me/553199540016"
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      aria-label="Contato via WhatsApp"
      data-testid="whatsapp-float-btn">

      <MessageCircle className="h-6 w-6" fill="white" />
    </a>);

};

// Navigation
const Navigation = ({ scrollToSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
  { label: "Benefícios", id: "benefits" },
  { label: "Módulos", id: "modules" },
  { label: "Mentor", id: "mentor" },
  { label: "Depoimentos", id: "testimonials" },
  { label: "FAQ", id: "faq" },
  { label: "Contato", id: "contact" }];


  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
      isScrolled ? "bg-[#2C3E30]/95 backdrop-blur-sm py-4" : "bg-transparent py-6"}`
      }
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="navigation">

      <div className="container mx-auto px-6 md:px-12 max-w-7xl flex justify-between items-center">
        <button
          onClick={() => scrollToSection('hero')}
          className="text-xl text-[#E8E4D9] hover:text-white transition-colors"
          data-testid="nav-logo">

          Bioarte
        </button>

        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) =>
          <button
            key={item.id}
            onClick={() => scrollToSection(item.id)}
            className="nav-link text-sm tracking-wider"
            data-testid={`nav-${item.id}`}>

              {item.label}
            </button>
          )}
        </div>

        <Button
          onClick={() => scrollToSection('contact')}
          className="hidden md:flex cta-primary text-sm py-2 px-4"
          data-testid="nav-cta">

          Agendar
        </Button>
      </div>
    </motion.nav>);

};

// Main App
function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: "vertical",
      gestureDirection: "vertical",
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false
    });

    function raf(time) {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenisRef.current?.destroy();
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      lenisRef.current?.scrollTo(element, { offset: -80 });
    }
  };

  return (
    <div className="App" data-testid="app-container">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#2C3E30',
            color: '#E8E4D9'
          }
        }} />

      <Navigation scrollToSection={scrollToSection} />
      <HeroSection scrollToSection={scrollToSection} />
      <BenefitsSection />
      <ModulesSection />
      <MentorSection />
      <TestimonialsSection />
      <FAQSection />
      <ContactSection />
      <Footer />
      <WhatsAppFloat />
    </div>);

}

export default App;